import React, { useState, useEffect, useCallback, useRef } from 'react';
import UniversalIconBtn from './headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { debounce } from '../util/debounce';
import ArrowDown from '../../asset/icon/below.png';
import { blink } from './Animation';
import { useEditMode } from '../hooks/useEditMode';

const DEBOUNCE_DELAY = 100;

const ScrollDownArrow: React.FC = () => {
  const { isEditMode } = useEditMode();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

      // 🔹 실제로 스크롤 가능한지 검사
    const hasScroll = container.scrollHeight > container.clientHeight;

    if (!hasScroll) {
      setIsVisible(false);
      setIsClosing(false);
      return;
    }

    const isAtTop = container.scrollTop <= 30;

    if (isAtTop) {
      // 위로 올라오면 숨김 타이머 제거 + 다시 보이게
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsVisible(true);
      setIsClosing(false);
    } else {
      setIsClosing(true);

      // 기존 타이머 있으면 정리
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        hideTimeoutRef.current = null;
      }, 500);
    }
  }, []);

  const debouncedHandleScroll = debounce(handleScroll, DEBOUNCE_DELAY);

  useEffect(() => {
    const findScrollableContainer = (): HTMLElement | null => {
      // 무조건 main에 scroll이 붙고 main 태그를 찾는다.
      const main = document.querySelector('main');
      return main instanceof HTMLElement ? main : null;
    };

    let retryCount = 0;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let container: HTMLElement | null = null;
    let mutationObserver: MutationObserver | null = null;

    // 스크롤 이벤트 리스너 (Event는 무시, ref만 사용)
    const onScroll: EventListener = () => {
      debouncedHandleScroll();
    };

    const setup = () => {
      container = findScrollableContainer();
  
      if (!container && retryCount < 3) {
        retryCount++;
        retryTimeoutId = setTimeout(setup, 500);
        return;
      }
  
      if (!container) {
        console.error('Scrollable container not found after retries');
        return;
      }
  
      containerRef.current = container;
  
      // 🔹 최초 한 번 상태 반영
      handleScroll();
  
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          handleScroll();
        });
  
        mutationObserver.observe(container, {
          childList: true,
          subtree: true,
        });
      }
  
      container.addEventListener('scroll', onScroll, { passive: true });
    };

    setup();

    return () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
  
      if (container) {
        container.removeEventListener('scroll', onScroll);
      }
  
      if (mutationObserver) {
        mutationObserver.disconnect();
      }

      debouncedHandleScroll.cancel();
  
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [handleScroll, debouncedHandleScroll]);

  const handleClick = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollDistance = Math.floor(container.scrollHeight / 10);
    container.scrollTo({
      top: scrollDistance,
      behavior: 'smooth',
    });
  }, []);


  if (!isVisible) return null;
  if (isEditMode) return null;

  return (
    <Arrow $isVisible={!isClosing}>
      <UniversalIconBtn
        size="standard"
        border={false}
        isTransparent
        backgroundImg={ArrowDown}
        onClick={handleClick}
      />
    </Arrow>
  );
};

const Arrow = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 1vh;
  left: 50%;
  transform: translateX(-50%);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  ${blink}

  z-index: 3;
`;

export default ScrollDownArrow;
